import React from "react";
import { useTranslation } from "react-i18next";

import { useAppContext } from "@/contexts";
import { convertLbToKg } from "@/utils/UnitUtils";
import { calculateBMIMetric } from "@/utils/bmi";
import { getMedBmiThresholds } from "@/utils/qualification/medBmiConfig";
import { shouldShowGlp1MedicationQuestion } from "@/utils/survey/glp1";

import {
  QuestionComponentProps,
  QuestionDefinition,
  QuestionOption,
} from "../../Question";
import { MultipleChoiceQuestion } from "../core";

const QUESTION_ID = "glp1Medication";

const GLP1_OPTION_IDS = [
  "yes",
  "prescriptionNotYetStarted",
  "noButConsidering",
  "no",
] as const;

type Glp1MedicationAnswer = (typeof GLP1_OPTION_IDS)[number];

const RETRIAGE_GLP1_OPTION_IDS: Glp1MedicationAnswer[] = ["yes", "no"];

declare module "@/contexts" {
  interface SurveyAnswers {
    glp1Medication?: Glp1MedicationAnswer;
  }
}

const Glp1MedicationQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const { enrollmentType } = useAppContext();
  const { t } = useTranslation();

  const optionIds =
    enrollmentType === "RETRIAGE" ? RETRIAGE_GLP1_OPTION_IDS : GLP1_OPTION_IDS;

  const options: QuestionOption[] = optionIds.map((id) => ({
    id,
    text: t(`glp1Medication.options.${id}`),
  }));

  return (
    <MultipleChoiceQuestion
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      options={options}
      questionId={QUESTION_ID}
      questionText={t("glp1Medication.questionText")}
      selectedItem={surveyAnswers[QUESTION_ID] || ""}
    />
  );
};

export const Q_GLP1_MEDICATION: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: (state) => shouldShowGlp1MedicationQuestion(state),
  component: Glp1MedicationQuestion,
};

export const Q_OUTCOMES_GLP1_MEDICATION: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({
    partnerInfo,
    surveyAnswers: { weight: weightInLbs },
    userProfile,
  }) => {
    const { currentProgram, heightCms, latestWeightKgs } = userProfile || {};

    // Only show for cardiometabolic programs
    if (currentProgram === "MOOD") {
      return false;
    }

    // Only show for BMI >= the partner's Med with-comorbidity threshold (default 27)
    const { minBmiWithComorbidity } = getMedBmiThresholds(partnerInfo);

    // Use BMI obtained from user profile & latest weigh-in if it is available
    if (heightCms !== undefined && latestWeightKgs !== undefined) {
      return (
        calculateBMIMetric(heightCms, latestWeightKgs) >= minBmiWithComorbidity
      );
    }

    // Fallback to BMI obtained from height on user profile (if available) and weight from survey
    if (heightCms !== undefined && weightInLbs !== undefined) {
      return (
        calculateBMIMetric(
          heightCms,
          convertLbToKg({
            mainUnitValue: weightInLbs,
          }).mainUnitValue,
        ) >= minBmiWithComorbidity
      );
    }

    // If we weren't able to calculate BMI for some reason default to showing question.
    return true;
  },

  component: Glp1MedicationQuestion,
};
